import React, { useEffect, useState } from 'react';
import {
  Factory,
  Plus,
  Edit3,
  Search,
  AlertCircle,
  FileText,
  MapPin,
  Mail,
  Phone,
  Package,
  CreditCard,
  CalendarClock,
  Landmark
} from 'lucide-react';
import { Supplier, SupplierPayload } from '../../../../types/Supplier.types';
import { FixedAsset } from '../../../../types/FixedAsset.types';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { useToast } from '../../../../hooks/useToast';
import Modal from '../../../../components/shared/Modal/Modal';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { Skeleton } from '../../../../components/shared/Skeleton/Skeleton';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import { listSuppliers, createSupplier, updateSupplier, deactivateSupplier } from '../../../../services/SuppliersService';
import { listFixedAssets } from '../../../../services/FixedAssetsService';
import { PAYMENT_METHOD_LABELS, PAYMENT_TERMS_LABELS } from '../../../../constants/clients';
import '../shared-modules.css';
import './SuppliersView.css';

// El backend guarda el plazo en días (payment_terms_days); el formulario usa las
// mismas claves que la ficha de cliente (ClientsView) para mantener el mismo selector.
const TERMS_TO_DAYS: Record<string, number> = { immediate: 0, '30_days': 30, '60_days': 60, '90_days': 90 };
const daysToTerms = (days: number | null): string => {
  if (days === 0) return 'immediate';
  if (days === 60) return '60_days';
  if (days === 90) return '90_days';
  return '30_days';
};

interface SupplierFormData extends Omit<SupplierPayload, 'paymentTermsDays'> {
  paymentTerms: string;
}

const EMPTY_FORM: SupplierFormData = {
  name: '',
  cif: '',
  email: '',
  phone: '',
  address: '',
  status: 'active',
  notes: '',
  paymentMethod: 'transfer',
  paymentTerms: '30_days',
  bankAccount: ''
};

export const SuppliersView = () => {
  const { notify } = useToast();

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [fixedAssets, setFixedAssets] = useState<FixedAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [assetsLoading, setAssetsLoading] = useState(true);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<SupplierFormData>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [deactivateModal, setDeactivateModal] = useState<{ id: string; name: string } | null>(null);

  const assetsForSupplier = (supplierId: string): FixedAsset[] =>
    fixedAssets.filter(a => a.supplierId === supplierId);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const list = await listSuppliers();
      setSuppliers(list);
      setListError('');
      setSelectedSupplier(prev => (prev ? list.find(s => s.id === prev.id) || null : null));
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setSuppliers([]);
      setSelectedSupplier(null);
      setListError(err instanceof Error ? err.message : 'No se pudo cargar el catálogo de acreedores.');
    } finally {
      setLoading(false);
    }
  };

  // Los activos fijos se piden completos y se filtran en cliente:
  // /fixed_assets_list no admite filtro por supplier_id.
  const fetchFixedAssets = async () => {
    try {
      setFixedAssets(await listFixedAssets());
    } catch (err) {
      console.error('Error fetching fixed assets:', err);
      setFixedAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchFixedAssets();
  }, []);

  const handleCreateNewClick = () => {
    setSelectedSupplier(null);
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      name: supplier.name,
      cif: supplier.cif,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address,
      status: supplier.status,
      notes: supplier.notes,
      paymentMethod: supplier.paymentMethod || 'transfer',
      paymentTerms: daysToTerms(supplier.paymentTermsDays),
      bankAccount: supplier.bankAccount
    });
    setIsEditing(true);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectSupplier = (supplier: Supplier) => {
    setIsFormOpen(false);
    setSelectedSupplier(supplier);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedSupplier(null);
  };

  // Clic fuera cierra la ficha. El formulario no: esta vista no avisa de
  // cambios sin guardar y se perdería lo escrito; se cierra con la X.
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedSupplier) && !isFormOpen);

  const handleSaveSupplier = async () => {
    if (!formData.name.trim() || !formData.cif.trim()) {
      setFormError('La razón social y el CIF son obligatorios.');
      return;
    }
    const cifRegex = /^[A-HJ-NP-SU-W][0-9]{8}$/i;
    if (!cifRegex.test(formData.cif.trim())) {
      setFormError('El formato de CIF no es válido (Ej: A82345678).');
      return;
    }
    const iban = (formData.bankAccount || '').replace(/\s/g, '').toUpperCase();
    if (iban && iban.length < 15) {
      setFormError('El código IBAN introducido no parece válido.');
      return;
    }

    const payload: SupplierPayload = {
      name: formData.name.trim(),
      cif: formData.cif.toUpperCase().trim(),
      email: formData.email?.trim(),
      phone: formData.phone?.trim(),
      address: formData.address?.trim(),
      status: formData.status,
      notes: formData.notes?.trim(),
      paymentMethod: formData.paymentMethod,
      paymentTermsDays: TERMS_TO_DAYS[formData.paymentTerms] ?? 30,
      bankAccount: iban
    };

    setIsSaving(true);
    try {
      if (isEditing && selectedSupplier) {
        const updated = await updateSupplier(selectedSupplier.id, payload);
        setSuppliers(prev => prev.map(s => (s.id === updated.id ? updated : s)));
        setSelectedSupplier(updated);
        notify('Éxito', 'Acreedor modificado exitosamente', 'info');
      } else {
        const created = await createSupplier(payload);
        setSuppliers(prev => [created, ...prev]);
        setSelectedSupplier(created);
        notify('Éxito', 'Acreedor dado de alta exitosamente', 'info');
      }
      setIsFormOpen(false);
    } catch (err) {
      console.error('Error saving supplier:', err);
      setFormError(err instanceof Error ? err.message : 'Error de comunicación con el servidor.');
    } finally {
      setIsSaving(false);
    }
  };

  // El backend no expone /suppliers_delete, así que la baja es lógica. Y un
  // proveedor con activos fijos asociados no se da de baja: la ficha del activo
  // quedaría apuntando a un proveedor inactivo.
  const handleDeactivateClick = (supplier: Supplier) => {
    const linked = assetsForSupplier(supplier.id);
    if (linked.length > 0) {
      notify(
        'Operación no permitida',
        `No se puede dar de baja "${supplier.name}": tiene ${linked.length} activo(s) fijo(s) asociado(s).`,
        'warning'
      );
      return;
    }
    setDeactivateModal({ id: supplier.id, name: supplier.name });
  };

  const confirmDeactivate = async () => {
    if (!deactivateModal) return;
    const { id } = deactivateModal;
    setDeactivateModal(null);
    try {
      const updated = await deactivateSupplier(id);
      setSuppliers(prev => prev.map(s => (s.id === updated.id ? updated : s)));
      if (selectedSupplier?.id === updated.id) setSelectedSupplier(updated);
      notify('Éxito', 'Acreedor dado de baja exitosamente', 'info');
    } catch (err) {
      console.error('Error deactivating supplier:', err);
      notify('Error', err instanceof Error ? err.message : 'Error al dar de baja el acreedor.', 'urgent');
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.cif.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns: ColumnDef<Supplier>[] = [
    {
      id: 'id',
      header: 'ACR_ID',
      render: (supplier) => <span className="suppliers-view__prov-id">{supplier.id}</span>
    },
    {
      id: 'name',
      header: 'NOMBRE / CIF',
      render: (supplier) => (
        <>
          <div className="suppliers-view__prov-name">{supplier.name}</div>
          <div className="suppliers-view__prov-cif">CIF: {supplier.cif}</div>
        </>
      )
    },
    {
      id: 'contact',
      header: 'CONTACTO',
      render: (supplier) => (
        <>
          <div className="suppliers-view__prov-email">{supplier.email || '—'}</div>
          <div className="suppliers-view__prov-phone">{supplier.phone || '—'}</div>
        </>
      )
    },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (supplier) => (
        <span className={`module-badge ${supplier.status}`}>{supplier.status}</span>
      )
    }
  ];

  const renderAssetsSkeleton = () => (
    <div className="suppliers-view__assets-list">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="suppliers-view__asset-item">
          <div className="skeleton-block skeleton-block--tight" style={{ flex: 1 }}>
            <Skeleton width="55%" height="12px" />
            <Skeleton width="35%" height="9px" />
          </div>
          <Skeleton width="60px" height="9px" />
        </div>
      ))}
    </div>
  );

  return (
    <>
      <MasterDetailLayout isOpen={Boolean(selectedSupplier || isFormOpen)} drawer>
        <div className="suppliers-view">
          <div className="module-header-row">
            <ModuleHeader
              title="Gestión_De_Acreedores"
              subtitle="Catálogo de acreedores para compras, gastos y adquisición de activos fijos."
            />
            <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
              <Plus size={14} /> Alta_Acreedor
            </button>
          </div>

          <div className="module-toolbar">
            <div className="module-search-wrapper">
              <Search className="module-search-icon" size={14} />
              <input
                type="text"
                placeholder="FILTRAR POR RAZÓN SOCIAL, CIF O ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="module-search-input"
              />
            </div>
          </div>

          {listError ? (
            <div className="module-panel">
              <div className="module-panel-empty">{listError}</div>
            </div>
          ) : (
            <DataTable
              data={filteredSuppliers}
              columns={columns}
              title={<><Factory size={14} className="module-panel-title-icon" /> Acreedores_Registrados</>}
              subtitle={loading ? undefined : `TOTAL: ${filteredSuppliers.length}`}
              isLoading={loading}
              getRowId={(supplier) => supplier.id}
              onRowClick={handleSelectSupplier}
              selectedRowId={selectedSupplier?.id}
              emptyText="No se encontraron acreedores registrados."
              actionsHeader="ACCIONES"
              actionsColumn={(supplier) => (
                <RowActionsMenu
                  actions={[
                    viewAction(() => handleSelectSupplier(supplier)),
                    editAction(() => handleEditClick(supplier)),
                    // La baja es lógica (no hay borrado en el backend).
                    deleteAction(() => handleDeactivateClick(supplier), {
                      label: 'Dar de baja',
                      disabled: supplier.status === 'inactive',
                      title: supplier.status === 'inactive' ? 'El acreedor ya está de baja' : undefined
                    })
                  ]}
                />
              )}
            />
          )}
        </div>

        {isFormOpen ? (
          <SidebarForm
            drawer
            title={isEditing ? 'Modificar_Ficha_Acreedor' : 'Alta_De_Nuevo_Acreedor'}
            onClose={closePanel}
            submitLabel={isSaving ? 'Guardando...' : 'Guardar_Acreedor'}
            submitDisabled={isSaving}
            onSubmit={handleSaveSupplier}
            showDelete={isEditing && Boolean(selectedSupplier)}
            deleteDisabled={selectedSupplier?.status === 'inactive'}
            deleteDisabledTooltip="El acreedor ya está de baja"
            onDelete={() => selectedSupplier && handleDeactivateClick(selectedSupplier)}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_Identificativos" icon={FileText}>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Razón Social *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, name: val })); }}
                    placeholder="Distribuciones Gráficas S.L."
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">CIF / NIF *</label>
                  <input
                    type="text"
                    required
                    value={formData.cif}
                    onChange={(e) => { const val = e.target.value.toUpperCase(); setFormData(prev => ({ ...prev, cif: val })); }}
                    placeholder="B12345678"
                    className="sidebar-form-input"
                  />
                </div>
              </div>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, email: val })); }}
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Teléfono</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, phone: val })); }}
                    className="sidebar-form-input"
                  />
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Estado</label>
                <select
                  value={formData.status}
                  onChange={(e) => { const val = e.target.value as 'active' | 'inactive'; setFormData(prev => ({ ...prev, status: val })); }}
                  className="sidebar-form-input"
                >
                  <option value="active">ACTIVO</option>
                  <option value="inactive">INACTIVO</option>
                </select>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Dirección Fiscal</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, address: val })); }}
                  className="sidebar-form-input"
                />
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Observaciones Internas</label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, notes: val })); }}
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>

            <DetailSection title="2. Condiciones_De_Pago" icon={CreditCard}>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Forma de Pago</label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, paymentMethod: val })); }}
                    className="sidebar-form-input"
                  >
                    <option value="transfer">TRANSFERENCIA BANCARIA</option>
                    <option value="direct_debit">GIRO DIRECTO / DOMICILIADO</option>
                    <option value="credit_card">TARJETA DE CRÉDITO</option>
                    <option value="cash">EFECTIVO</option>
                  </select>
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Plazo de Pago</label>
                  <select
                    value={formData.paymentTerms}
                    onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, paymentTerms: val })); }}
                    className="sidebar-form-input"
                  >
                    <option value="immediate">PAGO INMEDIATO</option>
                    <option value="30_days">30 DÍAS FECHA FACTURA</option>
                    <option value="60_days">60 DÍAS FECHA FACTURA</option>
                    <option value="90_days">90 DÍAS FECHA FACTURA</option>
                  </select>
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Cuenta Bancaria (IBAN)</label>
                <input
                  type="text"
                  value={formData.bankAccount}
                  onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, bankAccount: val })); }}
                  placeholder="ESXX 0000 0000 0000 0000 0000"
                  className="sidebar-form-input"
                />
              </div>
            </DetailSection>
          </SidebarForm>
        ) : selectedSupplier ? (
          <DetailPanel
            drawer
            title="Ficha_De_Acreedor"
            onClose={closePanel}
            headerActions={
              <DetailHeaderButton icon={Edit3} label="Editar" onClick={() => handleEditClick(selectedSupplier)} />
            }
            cards={
              <>
                <DetailCard
                  icon={Package}
                  title={`Activos_Fijos_Asociados ${assetsLoading ? '' : `(${assetsForSupplier(selectedSupplier.id).length})`}`}
                >
                  {assetsLoading ? (
                    renderAssetsSkeleton()
                  ) : assetsForSupplier(selectedSupplier.id).length === 0 ? (
                    <div className="suppliers-view__assets-empty">
                      Ningún activo fijo ligado a este acreedor
                    </div>
                  ) : (
                    <div className="suppliers-view__assets-list">
                      {assetsForSupplier(selectedSupplier.id).map(asset => (
                        <div key={asset.id} className="suppliers-view__asset-item">
                          <div>
                            <span className="suppliers-view__asset-name">{asset.name}</span>
                            <span className="suppliers-view__asset-code">
                              {asset.code}{asset.companyName ? ` • ${asset.companyName}` : ''}
                            </span>
                          </div>
                          <span className="suppliers-view__asset-date">{asset.acquisitionDate || '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </DetailCard>
              </>
            }
          >
            <DetailIdentity
              icon={Factory}
              reference={selectedSupplier.id}
              name={selectedSupplier.name}
              subtitle={<span className={`module-badge ${selectedSupplier.status}`}>{selectedSupplier.status}</span>}
            />

            <DetailFieldList>
              <DetailField icon={FileText} label="CIF_FACTURACIÓN">{selectedSupplier.cif}</DetailField>
              <DetailField icon={MapPin} label="DIRECCIÓN_FISCAL">{selectedSupplier.address || 'No declarada'}</DetailField>
              <DetailField icon={Phone} label="TELÉFONO">{selectedSupplier.phone || 'No declarado'}</DetailField>
              <DetailField icon={Mail} label="CORREO_ELECTRÓNICO">
                <span className="detail-field-value lowercase">{selectedSupplier.email || 'No declarado'}</span>
              </DetailField>
              <DetailField icon={CreditCard} label="FORMA_DE_PAGO">
                {selectedSupplier.paymentMethod
                  ? (PAYMENT_METHOD_LABELS[selectedSupplier.paymentMethod] || selectedSupplier.paymentMethod)
                  : 'No declarada'}
              </DetailField>
              <DetailField icon={CalendarClock} label="PLAZO_DE_PAGO">
                {selectedSupplier.paymentTermsDays === null
                  ? 'No declarado'
                  : PAYMENT_TERMS_LABELS[daysToTerms(selectedSupplier.paymentTermsDays)]}
              </DetailField>
              <DetailField icon={Landmark} label="CUENTA_BANCARIA">{selectedSupplier.bankAccount || 'No declarada'}</DetailField>
            </DetailFieldList>

            {selectedSupplier.notes && <DetailNotes>{selectedSupplier.notes}</DetailNotes>}
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>

      <Modal
        isOpen={!!deactivateModal}
        type="urgent"
        title="Dar de baja acreedor"
        message={`¿Está seguro de que desea dar de baja al acreedor "${deactivateModal?.name}"? Quedará marcado como inactivo.`}
        confirmText="Dar de baja"
        cancelText="Cancelar"
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateModal(null)}
      />
    </>
  );
};
